import type { PresentationBlock } from 'paintscout'

/**
 * Block types whose content always keeps some horizontal inset, never true edge-to-edge —
 * text-heavy or structured layouts where full bleed reads as broken, not a design choice. The
 * Content Width option is hidden for these types in GeneralOptions.tsx since toggling it would
 * have no visual effect (both columns resolve to the same padding for these types below).
 */
export const FIXED_PADDING_BLOCK_TYPES = [
  'text',
  '2-column',
  '3-column',
  'divider',
  'quote',
  'terms',
  'work-order',
  'pictures',
  'cards',
  'testimonials',
  'documents',
  'about-estimator'
]

export function getBlockPadding(args: {
  block: PresentationBlock
  source: 'presentation' | 'block'
  size: 'desktop' | 'tablet' | 'mobile'
  returnDefault?: boolean
  noTopPadding?: boolean
  contentWidth?: 'full' | 'md' | 'sm' | 'x-sm'
  horizontalOnly?: boolean
}): string {
  // tablet size is down('md'), mobile size is down('sm')
  const {
    block,
    source,
    size,
    returnDefault = false,
    noTopPadding = false,
    contentWidth: forceContentWidth,
    horizontalOnly
  } = args
  // v1's `background.noPadding` (its own `hasPadding` toggle) has no v2 equivalent — there is no
  // live v2 control for it, and a FIXED_PADDING_BLOCK_TYPES type must never lose its minimum inset
  // regardless of the flag, which a blanket zero-padding here defeated (#23882). The v1-to-v2
  // converter (carryEnvelope, shared.ts) now translates a v1 `noPadding: true` into a v2
  // `contentWidth` at conversion time instead, and drops the flag itself.
  const fields = block.fields ?? {}
  const sectionType = block.type

  // default values
  let horizontalAlign = 'center'
  let verticalAlign = 'center'
  let sectionHeight = 'sm'
  let contentWidth = 'full'

  if (!returnDefault) {
    if (fields.horizontalAlign) horizontalAlign = fields.horizontalAlign
    if (fields.verticalAlign) verticalAlign = fields.verticalAlign
    if (fields.sectionHeight) sectionHeight = fields.sectionHeight
    if (fields.contentWidth) contentWidth = fields.contentWidth
  }

  // force centering of insets for blocks below desktop size
  if (size !== 'desktop') {
    horizontalAlign = 'center'
  }

  // the "background image" of the poster layout must always be full width
  if (sectionType === 'banner' && block.fields.layout === 'poster' && source === 'presentation') {
    horizontalAlign = 'center'
    verticalAlign = 'center'
    sectionHeight = 'x-sm'
    contentWidth = 'full'
  }

  // md contentWidth === md sectionHeight
  // sm contentWidth === lg sectionHeight
  // lg contentWidth and xs sectionHeight are 0
  // xs contentWidth and sm sectionHeight are odd balls

  const alignmentFactor = 4
  const padding = {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0
  }

  const backgroundColor = block.background?.color
  const fullWidthImage2Col = sectionType === '2-column' && block.fields.fullWidthImage

  if (
    contentWidth === 'full' &&
    !['banner'].includes(sectionType) &&
    ((backgroundColor && !['#fff', '#ffffff'].includes(backgroundColor.toLowerCase())) || block.background?.useTheme)
  ) {
    contentWidth = 'md'
  }

  if (forceContentWidth) {
    contentWidth = forceContentWidth
  }

  let forcedInset = FIXED_PADDING_BLOCK_TYPES.includes(sectionType)

  if (sectionType === 'gallery' && block.fields?.files?.some((file) => file.format === 'pdf')) {
    forcedInset = true
  }

  if (fullWidthImage2Col) {
    forcedInset = false
    contentWidth = 'full'
    sectionHeight = 'x-sm'
  }

  if (sectionType === 'spacer' && sectionHeight === 'x-sm') sectionHeight = 'x-sm-spacer'

  // a forced-inset block's horizontal padding is meant to be fixed and predictable (that's the
  // point of FIXED_PADDING_BLOCK_TYPES/the gallery-pdf case above) — skewing it 1:3 toward one
  // edge via `horizontalAlign` has no editor control to set or undo it (only the AI generator's
  // `fieldsText.horizontalAlign` can), so an authored block ends up with visibly asymmetric,
  // seemingly-reduced padding on one side that no user ever chose (#24097)
  if (forcedInset) {
    horizontalAlign = 'center'
  }

  const horizontalPadding = {
    desktop: {
      'x-sm': 256,
      sm: 184,
      md: 112,
      full: !forcedInset ? 0 : 112
    },
    tablet: {
      'x-sm': 64,
      sm: 64,
      md: 64,
      full: !forcedInset ? 0 : 64
    },
    mobile: {
      'x-sm': 32,
      sm: 32,
      md: 32,
      full: !forcedInset ? 0 : 32
    }
  }
  const verticalPadding = {
    desktop: {
      'x-sm': 0,
      'x-sm-spacer': 24,
      sm: 48,
      md: 112,
      lg: 184
    },
    tablet: {
      'x-sm': 0,
      'x-sm-spacer': 24,
      sm: 48,
      md: 64,
      lg: 64
    },
    mobile: {
      'x-sm': 0,
      'x-sm-spacer': 16,
      sm: 32,
      md: 40,
      lg: 40
    }
  }

  if (horizontalAlign === 'left') {
    padding.left = horizontalPadding[size][contentWidth] / alignmentFactor
    padding.right = horizontalPadding[size][contentWidth] - horizontalPadding[size][contentWidth] / alignmentFactor
  }
  if (horizontalAlign === 'right') {
    padding.left = horizontalPadding[size][contentWidth] - horizontalPadding[size][contentWidth] / alignmentFactor
    padding.right = horizontalPadding[size][contentWidth] / alignmentFactor
  }
  if (horizontalAlign === 'center') {
    padding.left = horizontalPadding[size][contentWidth] / 2
    padding.right = horizontalPadding[size][contentWidth] / 2
  }
  if (verticalAlign === 'top') {
    padding.top = verticalPadding[size][sectionHeight] / alignmentFactor
    padding.bottom = verticalPadding[size][sectionHeight] - verticalPadding[size][sectionHeight] / alignmentFactor
  }
  if (verticalAlign === 'bottom') {
    padding.top = verticalPadding[size][sectionHeight] - verticalPadding[size][sectionHeight] / alignmentFactor
    padding.bottom = verticalPadding[size][sectionHeight] / alignmentFactor
  }
  if (verticalAlign === 'start') {
    padding.top = 0
    padding.bottom = verticalPadding[size][sectionHeight] / 2
  }
  if (verticalAlign === 'end') {
    padding.top = verticalPadding[size][sectionHeight] / 2
    padding.bottom = 0
  }
  if (verticalAlign === 'center') {
    padding.top = verticalPadding[size][sectionHeight] / 2
    padding.bottom = verticalPadding[size][sectionHeight] / 2
  }

  if (noTopPadding) {
    padding.top = 0
  }

  return `${horizontalOnly ? 0 : padding.top}px ${padding.right}px ${horizontalOnly ? 0 : padding.bottom}px ${
    padding.left
  }px`
}
